/**
 * Pending Meeting Reviews Hook
 *
 * Fetches auto-generated analyses with review_status='pending_review'.
 * Used by the MeetingReviewBanner and ActionableTasksWidget to surface
 * auto-processed meetings that need human review before actions (ClickUp
 * push, recap generation) can be taken.
 *
 * @returns Array of pending reviews sorted by creation date (newest first)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './useOrganization';
import { queryKeys } from '@/lib/query-keys';

export interface PendingMeetingReview {
  id: string;
  meeting_title: string | null;
  client_id: string;
  client_name: string;
  meeting_date: string;
  ai_summary: string | null;
  ai_new_tasks: unknown[] | null;
  auto_generated: boolean;
  review_status: 'pending_review';
  created_at: string;
  google_calendar_event_id: string | null;
}

export function usePendingMeetingReviews() {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: queryKeys.analyses.pendingReviews(organization?.id),
    queryFn: async (): Promise<PendingMeetingReview[]> => {
      if (!organization?.id) {
        throw new Error('No organization');
      }

      const { data, error } = await supabase
        .from('analyses')
        .select(`
          id,
          meeting_title,
          client_id,
          meeting_date,
          ai_summary,
          ai_new_tasks,
          auto_generated,
          review_status,
          created_at,
          google_calendar_event_id,
          clients(name)
        `)
        .eq('auto_generated', true)
        .eq('review_status', 'pending_review')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      return (data || []).map((row) => ({
        id: row.id,
        meeting_title: row.meeting_title,
        client_id: row.client_id,
        client_name: (row.clients as { name: string } | null)?.name || 'Unknown',
        meeting_date: row.meeting_date,
        ai_summary: row.ai_summary,
        ai_new_tasks: row.ai_new_tasks as unknown[] | null,
        auto_generated: row.auto_generated,
        review_status: row.review_status as 'pending_review',
        created_at: row.created_at,
        google_calendar_event_id: row.google_calendar_event_id,
      }));
    },
    enabled: !!organization?.id,
    staleTime: 60 * 1000, // 1 minute
  });
}
